import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import { runDate } from "./MethodNavigation";
import { Button } from "./components/ui/button";

/** The workspace name and its members. Everyone in a workspace shares its Methods, runs and corrections. */
export function Members({ workspace }: { workspace: string | undefined }) {
  const client = useQueryClient();
  const list = useQuery({ queryKey: ["members"], queryFn: api.members });
  const [name, setName] = useState<string>(), [email, setEmail] = useState(""), [notice, setNotice] = useState<string>();
  const refresh = () => client.invalidateQueries({ queryKey: ["members"] });
  const rename = useMutation({ mutationFn: api.setWorkspaceName, onSuccess: () => { setName(undefined); void client.invalidateQueries({ queryKey: ["session"] }); } });
  const invite = useMutation({
    mutationFn: api.invite,
    onSuccess: ({ invitation, email_sent }) => {
      setEmail("");
      setNotice(email_sent
        ? `Invitation sent to ${invitation.email}.`
        : `The invitation email could not be sent. Ask ${invitation.email} to sign up at ${window.location.origin} with that email address.`);
      void refresh();
    },
  });
  const cancel = useMutation({ mutationFn: api.cancelInvitation, onSuccess: refresh });
  const remove = useMutation({ mutationFn: api.removeMember, onSuccess: refresh });
  const error = rename.error ?? invite.error ?? cancel.error ?? remove.error ?? list.error;
  return <section className="api-keys members" aria-label="Members">
    <h2>Members</h2>
    <p className="list-muted">Everyone in {workspace ?? "this workspace"} shares its Methods, runs and corrections. A new person joins when they sign up with the invited email address. A person who already uses Method keeps their own workspaces and joins this one from the workspace menu.</p>
    <form className="list-controls" onSubmit={event => { event.preventDefault(); if (name?.trim()) rename.mutate(name.trim()); }}>
      <input aria-label="Workspace name" value={name ?? workspace ?? ""} maxLength={100} onChange={event => setName(event.target.value)} />
      <Button type="submit" variant="secondary" disabled={name === undefined || !name.trim() || name.trim() === workspace || rename.isPending}>Rename</Button>
    </form>
    <form className="list-controls" onSubmit={event => { event.preventDefault(); setNotice(undefined); if (email.trim()) invite.mutate(email.trim()); }}>
      <input aria-label="Email address to invite" type="email" placeholder="Email address" value={email} maxLength={320} onChange={event => setEmail(event.target.value)} />
      <Button type="submit" disabled={!email.trim() || invite.isPending}>Invite</Button>
    </form>
    {notice && <p role="status">{notice}</p>}
    {error && <p role="alert" className="account-setting-error">{error.message}</p>}
    {list.data && <div className="workspace-table-scroll"><table className="workspace-table">
      <thead><tr><th>Name</th><th>Email</th><th>Since</th><th /></tr></thead>
      <tbody>
        {list.data.members.map(member => <tr key={member.id}>
          <td>{member.name}{member.you && " (you)"}</td><td>{member.email}</td><td>{runDate(member.joined_at)}</td>
          <td>{!member.you && <Button variant="secondary" disabled={remove.isPending} onClick={() => { if (window.confirm(`Remove ${member.name}? They lose access to ${workspace ?? "this workspace"} at once.`)) remove.mutate(member.id); }}>Remove</Button>}</td>
        </tr>)}
        {list.data.invitations.map(invitation => <tr key={invitation.id}>
          <td className="list-muted">Invited by {invitation.invited_by}</td><td>{invitation.email}</td><td>{runDate(invitation.invited_at)}</td>
          <td><Button variant="secondary" disabled={cancel.isPending} onClick={() => cancel.mutate(invitation.id)}>Cancel</Button></td>
        </tr>)}
      </tbody>
    </table></div>}
  </section>;
}
